import classNames from 'classnames';
import React, { useEffect, useCallback, useState, useRef } from 'react';

import { Spinner } from '@trello/nachos/spinner';
import { HeaderTestIds } from '@trello/test-ids';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import {
  Key,
  registerShortcut,
  Scope,
  unregisterShortcut,
} from '@trello/keybindings';
import { isMac, isDesktop, isTouch } from '@trello/browser';
import { shouldHandleWindowsFrame } from '@trello/desktop';
import { useFeatureFlag } from '@trello/feature-flag-client';

import BoardList from './board-list';
import styles from './boards-menu.less';
import BoardsSearch from './boards-search';
import { BoardsMenuContainerProps } from './types';
import { BoardsMenuButton } from './boards-menu-button';

import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import {
  useBoardsMenuVisibility,
  BoardsMenuVisibilityState,
} from 'app/src/components/BoardsMenuVisibility';

import { useWorkspaceNavigation } from 'app/src/components/WorkspaceNavigation';

const format = forTemplate('boards_sidebar');
const formatRaw = forNamespace([], { shouldEscapeStrings: false });

export const BoardsMenu: React.FunctionComponent<BoardsMenuContainerProps> = ({
  clearSearchText,
  clearSelectedBoard,
  idMe,
  categoriesWithBoards,
  filteredBoards,
  isLoggedIn,
  isSearching,
  loading,
  searchText,
  selectedBoard,
  showCreateBoard,
  toggleClosedBoardDialog,
  visibleBoards,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    boardsMenuVisibility,
    setBoardsMenuVisibility,
  } = useBoardsMenuVisibility();

  const [{ enabled: workspaceNavigationEnabled }] = useWorkspaceNavigation();
  const isHeadsUpMsgEnabled = useFeatureFlag(
    'teamplates.web.boards-menu-heads-up-message',
    false,
  );

  const [shouldFocusInput, setShouldFocusInput] = useState(true);

  const isBoardsMenuPinned =
    boardsMenuVisibility === BoardsMenuVisibilityState.PINNED;
  const isBoardsMenuVisible =
    boardsMenuVisibility !== BoardsMenuVisibilityState.CLOSED;

  const focusInputIfNecessary = useCallback(() => {
    if (shouldFocusInput) {
      searchRef?.current?.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput, searchRef]);

  const toggleBoardsMenuPopover = useCallback(() => {
    setBoardsMenuVisibility(
      boardsMenuVisibility === BoardsMenuVisibilityState.POPOVER
        ? BoardsMenuVisibilityState.CLOSED
        : BoardsMenuVisibilityState.POPOVER,
    );
  }, [boardsMenuVisibility, setBoardsMenuVisibility]);

  const onBKeypress: React.KeyboardEventHandler<HTMLElement> = useCallback(() => {
    // Do not attempt to open the boards menu unless the user is
    // logged in OR we are showing the workspace navigation
    if (!idMe) {
      return;
    }

    if (boardsMenuVisibility !== BoardsMenuVisibilityState.PINNED) {
      toggleBoardsMenuPopover();

      Analytics.sendPressedShortcutEvent({
        shortcutName: 'boardsMenuShortcut',
        keyValue: 'b',
        source: getScreenFromUrl(),
      });

      if (boardsMenuVisibility === BoardsMenuVisibilityState.CLOSED) {
        Analytics.sendScreenEvent({
          name: 'boardsMenuInlineDialog',
          attributes: {
            method: 'boardsMenuShortcut',
          },
        });
      } else {
        Analytics.sendClosedComponentEvent({
          componentName: 'boardsMenuInlineDialog',
          componentType: 'inlineDialog',
          source: getScreenFromUrl(),
          attributes: {
            method: 'boardsMenuShortcut',
          },
        });
      }
    }

    setShouldFocusInput(true);
  }, [idMe, boardsMenuVisibility, toggleBoardsMenuPopover]);

  const onEscapeKeypress: React.KeyboardEventHandler<HTMLElement> = useCallback(() => {
    if (boardsMenuVisibility === BoardsMenuVisibilityState.POPOVER) {
      toggleBoardsMenuPopover();
      Analytics.sendPressedShortcutEvent({
        shortcutName: 'escapeShortcut',
        source: 'boardsMenuInlineDialog',
        keyValue: 'esc',
      });
      Analytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: 'boardsMenuInlineDialog',
        source: getScreenFromUrl(),
        attributes: {
          method: 'escapeShortcut',
        },
      });
    }
  }, [boardsMenuVisibility, toggleBoardsMenuPopover]);

  const onDocumentClick = useCallback(
    (e: MouseEvent) => {
      const target: Element = e.target as Element;

      if (boardsMenuVisibility !== BoardsMenuVisibilityState.POPOVER) {
        return;
      }

      const isOutsideClick = !ref.current?.contains(target);

      // If the click occurred on a board tile, close the menu. Ideally this
      // would happen in the board tiles
      if (!isOutsideClick && target.closest('a[href]')) {
        setBoardsMenuVisibility(BoardsMenuVisibilityState.CLOSED);
      }

      // If the click occurred outside the boards menu container, treat as a
      // dismissal and close
      if (isOutsideClick) {
        setBoardsMenuVisibility(BoardsMenuVisibilityState.CLOSED);
        Analytics.sendClosedComponentEvent({
          componentType: 'inlineDialog',
          componentName: 'boardsMenuInlineDialog',
          source: getScreenFromUrl(),
          attributes: {
            method: 'clicked outside of inline dialog',
          },
        });
      }
    },
    [boardsMenuVisibility, ref, setBoardsMenuVisibility],
  );

  const onCreateBoardButtonClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'createBoardLink',
      source: 'boardsMenuInlineDialog',
      attributes: {
        whileSearching: isSearching,
      },
    });

    showCreateBoard();
    setBoardsMenuVisibility(BoardsMenuVisibilityState.CLOSED);
  }, [isSearching, setBoardsMenuVisibility, showCreateBoard]);

  const onPinBoardsMenuButtonClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'alwaysKeepMenuOpenLink',
      source: 'boardsMenuInlineDialog',
      attributes: {
        wasPreviouslyPinned: isBoardsMenuPinned,
      },
    });

    setBoardsMenuVisibility(
      isBoardsMenuPinned
        ? BoardsMenuVisibilityState.POPOVER
        : BoardsMenuVisibilityState.PINNED,
    );
  }, [isBoardsMenuPinned, setBoardsMenuVisibility]);

  const onShowClosedBoardsButtonClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'seeClosedBoardsLink',
      source: 'boardsMenuInlineDialog',
    });

    toggleClosedBoardDialog();
    setBoardsMenuVisibility(BoardsMenuVisibilityState.CLOSED);
  }, [setBoardsMenuVisibility, toggleClosedBoardDialog]);

  useEffect(() => {
    switch (boardsMenuVisibility) {
      case BoardsMenuVisibilityState.CLOSED:
        registerShortcut(onBKeypress, { scope: Scope.Header, key: Key.b });
        clearSearchText();
        clearSelectedBoard();
        setShouldFocusInput(true);
        break;
      case BoardsMenuVisibilityState.POPOVER:
        Analytics.sendScreenEvent({
          name: 'boardsMenuInlineDialog',
        });
        document.addEventListener('click', onDocumentClick);
        registerShortcut(onEscapeKeypress, {
          scope: Scope.Popover,
          key: Key.Escape,
        });
        focusInputIfNecessary();
        break;
      case BoardsMenuVisibilityState.PINNED:
        registerShortcut(onBKeypress, { scope: Scope.Header, key: Key.b });
        focusInputIfNecessary();
        break;
      default:
        break;
    }

    return () => {
      document.removeEventListener('click', onDocumentClick);
      unregisterShortcut(onEscapeKeypress);
      unregisterShortcut(onBKeypress);
    };
  }, [
    boardsMenuVisibility,
    clearSearchText,
    clearSelectedBoard,
    setShouldFocusInput,
    onBKeypress,
    onEscapeKeypress,
    onDocumentClick,
    focusInputIfNecessary,
  ]);

  const renderHeadsUpMsg = () => {
    const sidebarHelpUrl =
      'https://community.atlassian.com/t5/Trello-articles/Get-a-first-look-at-Trello-s-new-sidebar/ba-p/1587343';

    if (!isHeadsUpMsgEnabled) {
      return null;
    }

    return (
      <div className={styles.sidebarHeadsUp}>
        {format(
          'hey-trello-is-getting-an-upgrade-and-this-boards-menu-is-moving-soon',
        )}{' '}
        <a href={sidebarHelpUrl} target="_blank" rel="noopener">
          {format('preview-the-upcoming-trello-sidebar-now')}
        </a>
      </div>
    );
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={classNames(
        styles.boardsDrawer,
        !isBoardsMenuPinned && styles.boardsDrawerPopover,
        shouldHandleWindowsFrame() && styles.desktopWin,
      )}
      hidden={!isBoardsMenuVisible || workspaceNavigationEnabled}
    >
      <div
        className={classNames(
          styles.boardsDrawerHeader,
          isDesktop() && isMac() && styles.desktopMac,
        )}
        hidden={!isBoardsMenuPinned}
      >
        {format('boards')}
      </div>
      <div className={styles.boardsMenuWrapper}>
        <div
          className={styles.boardsMenu}
          data-test-id={HeaderTestIds.BoardsMenuPopover}
        >
          {renderHeadsUpMsg()}
          <BoardsSearch
            ref={searchRef}
            boards={filteredBoards}
            searchText={searchText}
            selectedBoard={selectedBoard}
            visibleBoards={visibleBoards}
            boardsMenuVisibility={boardsMenuVisibility}
            setBoardsMenuVisibility={setBoardsMenuVisibility}
          />
          <BoardList
            categories={categoriesWithBoards}
            hidden={isSearching || loading}
            boardsMenuVisibility={boardsMenuVisibility}
            setBoardsMenuVisibility={setBoardsMenuVisibility}
          />
          {loading && <Spinner>{format('boards')}</Spinner>}
          <div className={styles.buttons}>
            <BoardsMenuButton
              onClick={onCreateBoardButtonClick}
              className={classNames(isSearching && styles.createBoardButton)}
              testId={HeaderTestIds.BoardsMenuCreateBoard}
            >
              {isSearching
                ? formatRaw('add board named', {
                    boardName: searchText,
                  })
                : format('create-new-board-ellipsis')}
            </BoardsMenuButton>

            {!isSearching && (
              <>
                {!isTouch() && (
                  <BoardsMenuButton
                    onClick={onPinBoardsMenuButtonClick}
                    testId={HeaderTestIds.BoardsMenuPin}
                  >
                    {!isBoardsMenuPinned
                      ? format('always-keep-this-menu-open')
                      : format('don-t-keep-this-menu-open')}
                  </BoardsMenuButton>
                )}
                <BoardsMenuButton
                  onClick={onShowClosedBoardsButtonClick}
                  testId={HeaderTestIds.BoardsMenuOpenClosed}
                >
                  {format('see-closed-boards-ellipsis')}
                </BoardsMenuButton>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
